import order from '@apis/order'
import { outlinestore } from '@assets/icons'
import Status from '@components/status'
import Table from '@components/table'
import DateRangeFilter from '@components/table/DateRangeFilter'
import StatusFilter from '@components/table/StatusFilter'
import styled from '@emotion/styled'
import { RootState } from '@store/index'
import theme from '@utils/theme'

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;

  @media (max-width: 768px) {
    flex-wrap: wrap;
    width: 100%;
  }
`
import dayjs from 'dayjs'
import React, { Fragment, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import {
  AddOnPoints,
  AddOnWrapper,
  Avatar,
  CustomerDetails,
  CustomerWrapper,
  DateText,
  DateWrapper,
  Email,
  FieldText,
  FieldTwoWrapper,
  Name,
} from './style'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import Column from '@components/column'
import IndividualUser from '@pages/admin/user-management/individual-user'
import { useModal } from '@context/ModalContext'

dayjs.extend(utc)
dayjs.extend(timezone)
const OrderList = ({ userId }: { userId?: string }) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [status, setStatus] = useState('')
  const {
    items: orders,
    totalItems,
    totalPages,
    loading,
  } = useSelector((state: RootState) => state.orders)

  const [page, setPage] = useState(1)

  const fetchOrders = async (search = '') => {
    try {
      await order.list({
        state: 'orders',
        params: {
          page,
          limit: 30,
          userId: userId ?? null,
          type: 'Store',
          ...(search && { search }),
          ...(fromDate && { startDate: fromDate }),
          ...(toDate && { endDate: toDate }),
          ...(status && { status }),
        },
      })
    } catch (error) {}
  }
  const {showModal} = useModal()
  const columns = [
    {
      title: 'Customer',
      key: 'customer',
      render: (item) => {
        const initials = item.customer.name
          ? item.customer.name
              .split(' ')
              .map((n: string) => n[0])
              .join('')
              .toUpperCase()
          : 'U'

        return (
          <CustomerWrapper
            onClick={() => {
              if (item?.customer?.id) {
                showModal(<IndividualUser userStateId={item?.customer?.id} />, {
                  width: 1200,
                })
              }
            }}
          >
            {item.customer.avatar ? (
              <Avatar
                src={`https://admin.tournaop.com/${item.customer.avatar}`}
                alt="avatar"
              />
            ) : (
              <div
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: '50%',
                  backgroundColor: '#ccc',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 'bold',
                  color: '#fff',
                  fontSize: '0.9rem',
                }}
              >
                {initials}
              </div>
            )}

            <CustomerDetails>
              <Name>{item.customer.name}</Name>
              <Email>{item.customer.email}</Email>
            </CustomerDetails>
          </CustomerWrapper>
        )
      },
    },
    {
      title: 'Product',
      key: 'product',
    },
    {
      title: 'Field One',
      key: 'field_one',
      render: (item) => <p>{item?.field_one}</p>,
    },
    {
      title: 'Field Two',
      key: 'field_two',
      render: (item) => (
        <FieldTwoWrapper>
          <FieldText>{item?.field_two}</FieldText>
        </FieldTwoWrapper>
      ),
    },
    {
      title: 'Add-Ons',
      key: 'add_ons',
      render: (item) => (
        <AddOnWrapper>
          <p>{item.add_ons?.name}</p>
        </AddOnWrapper>
      ),
    },
    {
      title: 'Points',
      key: 'add_ons_points',
      render: (item) => (
        <AddOnWrapper>
          <AddOnPoints>({item.add_ons_points ?? item.add_ons?.points})</AddOnPoints>
        </AddOnWrapper>
      ),
    },
    {
      title: 'Order Date',
      key: 'order_date',
      render: (item) => (
        <Column stylecolumn="row-gap:2px">
          <p>{item.created_date}</p>
          <p style={{ color: 'gray', fontSize: '12px' }}>{item.created_time}</p>
        </Column>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      render: (item) => (
        <Status
          item={item}
          statuses={['Pending', 'Delivered', 'Cancelled']}
          onStatusChange={async (status) => {
            await order.updateStatus({
              id: item.id,
              state: 'orders',
              data: { status },
            })
          }}
        />
      ),
    },
  ]

  const data = orders.map((order) => {
    const infos = order?.infos || {}
    const externalType = infos?.type as 'smm' | 'aluu' | undefined
    const orderResponse = infos?.orderResponse || {}

    let product = '-'
    let field_one = '-'
    let field_two = '-'
    let add_ons: any = order.addons
    let add_ons_points: number | string | undefined = order.addons?.points

    if (externalType === 'smm') {
      product = orderResponse?.serviceName || `Service #${orderResponse?.serviceId ?? '-'}`
      field_one = orderResponse?.link ? ` ${orderResponse.link}` : '-'
      field_two = orderResponse?.quantity !== undefined ? ` Qty: ${orderResponse.quantity}` : '-'
      add_ons = undefined
      add_ons_points = order.points
    } else if (externalType === 'aluu') {
      product = orderResponse?.productName || `${orderResponse?.game ?? '-'} ${orderResponse?.denom ?? ''}`.trim()
      field_one = orderResponse?.userid ? ` User ID: ${orderResponse.userid}` : '-'
      field_two = orderResponse?.serverid ? ` Server: ${orderResponse.serverid}` : '-'
      add_ons = undefined
      add_ons_points = order.points
    } else {
      const infoKeys = Object.keys(infos)
      const field_one_key = infoKeys[0] || null
      const field_two_key = infoKeys[1] || null
      product = order.addons?.product?.name ?? '-'
      field_one = field_one_key ? ` ${infos[field_one_key]}` : '-'
      field_two = field_two_key ? ` ${infos[field_two_key]}` : '-'
    }

    return {
      id: order.id,
      customer: order.user,
      product,
      field_one,
      field_two,
      add_ons,
      add_ons_points,
      created_date: order.createdAt ? order.createdAt.split('T')[0] : 'N/A',
      created_time: order.createdAt
        ? (() => {
            const [hour, min, sec] = order.createdAt
              .split('T')[1]
              .split('.')[0]
              .split(':')
            const h = Number(hour)
            const ampm = h >= 12 ? 'PM' : 'AM'
            const hour12 = h % 12 === 0 ? 12 : h % 12
            return `${hour12}:${min}:${sec} ${ampm}`
          })()
        : 'N/A',
      status: order.status,
    }
  })
  useEffect(() => {
    setPage(1)
  }, [searchTerm, fromDate, toDate, status])
  useEffect(() => {
    fetchOrders(searchTerm)
  }, [page, searchTerm, fromDate, toDate, status])

  return (
    <Fragment>
      <Table
        onSearchChange={(val) => {
          setSearchTerm(val)
        }}
        loading={loading}
        totalItems={totalItems}
        totalPages={totalPages}
        pageControlled={page}
        onPageChange={setPage}
        itemsPerPage={30}
        data={data}
        text="Order List"
        icon={outlinestore}
        columns={columns}
        searchKeys={['name', 'number', 'points']}
        rightSearchElement={
          <HeaderActions>
            <DateRangeFilter
              fromDate={fromDate}
              toDate={toDate}
              onFromDateChange={setFromDate}
              onToDateChange={setToDate}
            />
            <StatusFilter value={status} onChange={setStatus} />
          </HeaderActions>
        }
      />
    </Fragment>
  )
}

export default OrderList
